// @flow
import * as React from 'react';
import styled from 'styled-components';
import Flex from 'components/Flex';
import Box from 'components/Box';
import ToggleHelperContainer from 'containers/ToggleHelperContainer';
import AmpContext from 'utils/AmpContext';
import { RichText } from 'components/Text';
import { ArrowLink } from 'components/Link';
import { setupLinkTracker } from 'utils/analytics';
import { fontSizesByType } from '../theme';

const { useRef, useEffect, useState } = React;

const PrintBox = styled(Box)`
  @media print {
    display: ${props => props.$printDisplay};
  }
`;

const Container = styled.div`
  ${props =>
    props.$showAll
      ? `
    overflow-anchor: none;
  `
      : ''}
`;

const TruncatedHtml = ({
  html,
  truncatedHtml,
  render,
  alwaysTruncate
}: {
  html: string,
  truncatedHtml: string,
  render: ({
    truncated: boolean,
    truncatedHtml: string,
    showAll: boolean,
    toggleShowAll: () => void
  }) => React.Node,
  alwaysTruncate?: boolean
}) => {
  if (html === truncatedHtml || (!truncatedHtml && !alwaysTruncate)) {
    return render({
      truncated: false,
      truncatedHtml,
      showAll: true,
      toggleShowAll: () => {}
    });
  }
  return (
    <ToggleHelperContainer>
      {(showAll, toggleShowAll) =>
        render({
          truncated: true,
          truncatedHtml,
          showAll,
          toggleShowAll
        })
      }
    </ToggleHelperContainer>
  );
};

export default TruncatedHtml;

export const TruncatedHtmlWithLinks = ({
  html,
  truncatedHtml,
  showMoreLinkTitle,
  showLessLinkTitle,
  renderHiddenLink,
  simple,
  eventName,
  eventProperties,
  alwaysTruncate
}: {
  html: string,
  truncatedHtml: string,
  showMoreLinkTitle: string,
  showLessLinkTitle: string,
  renderHiddenLink?: () => React.Node,
  simple?: boolean,
  eventName?: string,
  eventProperties?: Object,
  alwaysTruncate?: boolean
}) => {
  const ref = useRef();
  const truncatedRef = useRef();
  const [linksAreTracked, setLinksAreTracked] = useState(false);

  useEffect(
    () => {
      if (eventProperties && eventName && !linksAreTracked) {
        [ref, truncatedRef].forEach(r => {
          if (r && r.current) {
            const linkEls = [...r.current.getElementsByTagName('a')];
            linkEls.forEach(link => {
              if (
                link.hasAttribute('href') &&
                link.getAttribute('href') !== '#'
              ) {
                setupLinkTracker(eventName, eventProperties)(link);
              }
            });
          }
        });
        setLinksAreTracked(true);
      }
    },
    [eventProperties, eventName, linksAreTracked, setLinksAreTracked]
  );

  return (
    <AmpContext.Consumer>
      {ampEnabled =>
        ampEnabled ? (
          <RichText dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <TruncatedHtml
            html={html}
            truncatedHtml={truncatedHtml}
            alwaysTruncate={alwaysTruncate}
            render={({
              truncated,
              truncatedHtml: truncatedHtmlForRender,
              showAll,
              toggleShowAll
            }) => (
              <Container $showAll={showAll}>
                <PrintBox
                  display={showAll ? 'block' : 'none'}
                  $printDisplay="block"
                >
                  <div ref={ref}>
                    <RichText
                      dangerouslySetInnerHTML={{ __html: html }}
                      normalize={!simple}
                      simple={simple}
                    />
                  </div>
                </PrintBox>

                <PrintBox
                  display={showAll ? 'none' : 'block'}
                  $printDisplay="none"
                >
                  <div ref={truncatedRef}>
                    <RichText
                      dangerouslySetInnerHTML={{
                        __html: truncatedHtmlForRender
                      }}
                      normalize={!simple}
                      simple={simple}
                    />
                  </div>
                </PrintBox>

                <PrintBox $printDisplay="none">
                  {truncated ? (
                    <Flex flexWrap="wrap">
                      <Box
                        width={[1, 1, 1, 'auto']}
                        fontSize={fontSizesByType.body}
                      >
                        <ArrowLink
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            toggleShowAll();
                          }}
                          open={showAll}
                        >
                          {showAll ? showLessLinkTitle : showMoreLinkTitle}
                        </ArrowLink>
                      </Box>
                      {renderHiddenLink && showAll && (
                        <Box
                          width={[1, 1, 1, 'auto']}
                          ml={[0, 0, 0, 'auto']}
                          mt={[4, 4, 4, 0]}
                        >
                          {renderHiddenLink()}
                        </Box>
                      )}
                    </Flex>
                  ) : (
                    <Flex>
                      {renderHiddenLink && (
                        <Box ml={[0, 0, 0, 'auto']}>{renderHiddenLink()}</Box>
                      )}
                    </Flex>
                  )}
                </PrintBox>
              </Container>
            )}
          />
        )
      }
    </AmpContext.Consumer>
  );
};
